const SESSION_KEY = 'dz_analytics_sid';
const DEVICE_KEY = 'dz_analytics_did';

type AnalyticsEventType = 'PAGE_VIEW' | 'ADD_TO_CART' | 'BEGIN_CHECKOUT' | 'PURCHASE';

type TrackPayload = {
  type: AnalyticsEventType;
  path?: string;
  productId?: string;
  orderId?: string;
  metadata?: Record<string, unknown>;
};

function apiBase(): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
  return base;
}

function sessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'anonymous';
  }
}

/** ID estável do aparelho/navegador (persiste entre sessões). */
function deviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

function deviceClass(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
  return 'desktop';
}

function shouldSkipPath(path: string): boolean {
  return path.startsWith('/admin') || path.startsWith('/conta') || path.startsWith('/auth');
}

/** Fire-and-forget — nunca bloqueia UX. */
export function track(event: TrackPayload): void {
  if (typeof window === 'undefined') return;
  const path = event.path ?? `${window.location.pathname}${window.location.search}`;
  if (event.type === 'PAGE_VIEW' && shouldSkipPath(window.location.pathname)) return;

  const body = {
    type: event.type,
    path,
    sessionId: sessionId(),
    deviceId: deviceId(),
    productId: event.productId,
    orderId: event.orderId,
    metadata: {
      device: deviceClass(),
      ...event.metadata,
    },
  };

  const url = `${apiBase()}/api/v1/analytics/events`;

  try {
    const json = JSON.stringify(body);
    if (navigator.sendBeacon) {
      const blob = new Blob([json], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: json,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // ignore
  }
}

export function trackPageView(path?: string): void {
  track({ type: 'PAGE_VIEW', path });
}

export function trackAddToCart(productId: string, metadata?: Record<string, unknown>): void {
  track({ type: 'ADD_TO_CART', productId, metadata });
}

export function trackBeginCheckout(metadata?: Record<string, unknown>): void {
  track({ type: 'BEGIN_CHECKOUT', path: '/checkout', metadata });
}

export function trackPurchase(orderId: string, metadata?: Record<string, unknown>): void {
  track({ type: 'PURCHASE', orderId, metadata });
}
